import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { CoverImagesProjectResource } from '@/types/resources';
import { api } from '@/utils/api';

const AiText = styled(Text)<{ $active?: boolean }>`
  text-align: left;
  margin-bottom: 10px;
  padding: 12px 20px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:active {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  ${({ $active }) =>
    $active &&
    css`
      border-color: ${({ theme }) => theme.colors.purple};
    `}
`;

const StyledButton = styled(Button)`
  margin: 20px 0 10px;
`;

interface Props {
  aiTexts:
    | {
        id: number;
        text: string;
      }[]
    | undefined;
  selectedTexts: number[];
  project: CoverImagesProjectResource;
  setSelectedTexts: React.Dispatch<React.SetStateAction<number[]>>;
}

const AiGeneratedTexts: FC<Props> = ({
  aiTexts,
  selectedTexts,
  project,
  setSelectedTexts,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const queryClient = useQueryClient();
  const alerts = useAlerts();
  const { id } = useParams();

  const onGenerateMoreTexts = async () => {
    setIsGenerating(true);
    try {
      await api.post<any, { projectId: number }>(
        '/v1/cover-images/generate-more-ai-texts',
        {
          project_id: id,
          article_id: project.articleId,
          is_open_list: project.isOpenList,
        },
      );

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      await queryClient.invalidateQueries(
        ['cover_images_project_ai_content', { id }],
        {
          exact: true,
        },
      );
    } catch (err) {
      alerts.error((err as any)?.response?.data?.message);
    }
    setIsGenerating(false);
  };

  return (
    <>
      {aiTexts?.map((text, index) => (
        <AiText
          $type="smallBold"
          key={index}
          $active={selectedTexts.includes(text.id)}
          onClick={() => {
            setSelectedTexts((prev) => {
              if (!prev.includes(text.id)) {
                return [...prev, text.id];
              }
              return prev.filter((element) => element !== text.id);
            });
          }}
        >
          {text.text}
        </AiText>
      ))}
      <StyledButton
        loading={isGenerating}
        disabled={isGenerating}
        onClick={() => onGenerateMoreTexts()}
      >
        {isGenerating ? 'Generating...' : 'Generate More Texts'}
      </StyledButton>
    </>
  );
};

export default AiGeneratedTexts;
